import React from 'react';
import { cn } from '@/lib/utils';

/*
 * Dashkit joins grouped buttons through `.btn-group > .btn` descendant
 * selectors in src/vendor/dashkit/css/theme.bundle.css. The shadcn Button does
 * not emit `.btn`, so a react-bootstrap <ButtonGroup> wrapping migrated buttons
 * silently loses both the sizing and the joining (DX-884).
 *
 * This reproduces the effective rules against direct children instead:
 *   .btn-group{display:inline-flex;position:relative;vertical-align:middle;border-radius:.375rem}
 *   .btn-group>.btn{flex:1 1 auto;position:relative}
 *   .btn-group>.btn:hover{z-index:1}
 *   .btn-group>.btn:not(:first-child){margin-left:-1px}
 *   .btn-group>.btn:not(:last-child):not(.dropdown-toggle){border-*-right-radius:0}
 *   .btn-group>:not(.btn-check)+.btn{border-*-left-radius:0}
 *
 * Size is not propagated — Bootstrap did it via `.btn-group-sm > .btn`, which
 * is the same descendant trick. Pass `size` on each Button instead.
 */
const ButtonGroup = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    role="group"
    className={cn(
      'tw:relative tw:inline-flex tw:align-middle tw:rounded-md',
      'tw:[&>*]:relative tw:[&>*]:flex-auto',
      'tw:[&>*:hover]:z-[1] tw:[&>*:focus-visible]:z-[2]',
      'tw:[&>*:not(:first-child)]:-ml-px',
      'tw:[&>*:not(:last-child)]:rounded-r-none',
      'tw:[&>*:not(:first-child)]:rounded-l-none',
      className,
    )}
    {...props}
  />
));
ButtonGroup.displayName = 'ButtonGroup';

export { ButtonGroup };
